<template>
    <div class="p-4 flex-2 z-50">
        <div>
            <h1 class="text-3xl text-orange-600">
                {{ $t("home.meetTheTeam").toUpperCase() }}
            </h1>
            <div
                class="mt-4"
                v-html="`<p>${$t('aboutUs.meetTheTeam')}</p>`"
            ></div>
        </div>
        <br />
        <h1 class="text-3xl text-orange-600">{{ $t("aboutUs.doctors") }}</h1>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <DoctorCard
                v-for="doctor in doctors.filter((d) => !d.isResearcher)"
                :key="doctor.id"
                :doctor="doctor"
            />
        </div>
        <h1 class="text-3xl text-orange-600 mt-8">
            {{ $t("aboutUs.scientificBoard") }}
        </h1>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <DoctorCard
                v-for="doctor in doctors.filter((d) => d.isResearcher)"
                :key="doctor.id"
                :doctor="doctor"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import DoctorCard from "~/components/DoctorCard.vue";
import { useHead } from "#imports";

useHead({
    title: "Meet the Team - Cardioteam",
    meta: [
        {
            name: "description",
            content: "Cardioteam doctors and researchers",
        },
        {
            name: "keywords",
            content: "Cardioteam, doctors, researchers",
        },
        {
            name: "author",
            content: "Cardioteam",
        },
        {
            name: "og:title",
            content: "Meet the Team - Cardioteam",
        },
        {
            name: "og:description",
            content: "Cardioteam doctors and researchers",
        },
    ],
});

const doctors = [
    {
        id: 3,
        name: "Aleksandra G. Grdinić",
        image: "/images/doctor_orange_aleksandra_grdinic.jpg",
        shortBio:
            "PhD, specialist in internal medicine and cardiology. Member of American board for regenerative medicine",
        fullBio: `Aleksandra was born in 1971. She is currently working as a Senior consultant in cardiology and internal medicine and researcher at
            Sykehuset Østfold Kalnes in Sarpsborg, Norway. During 25 years of experience as a specialist in internal medicine (form 2001)  and afterwards specialist in cardiology (from 2007)  she gained extensive experience in diagnosis and treatment of a wide range of cardiovascular conditions, such as coronary heart disease, heart failure, valvular heart disease, arterial hypertension, rhythm disorders, and other disorders of the cardiovascular system, through work in the cardiac ward and outpatients ward. During her research work she performed both in her home country (Montenegro and Serbia) and abroad (Norway, Italy, England, UEA, Malta) she gained grade Master of science, with thesis "Prognosis of malignant arrhythmias in acute myocardial infarction " , Doctor of science, PhD with thesis "Biochemical markers and genetic predisposition of resistance to antiplatelet agents in patients undergoing elective percutaneous coronary intervention" (2007) and postdoc studies in Norway with the project ” Predicting the risk of recurrent venous thromboembolism and bleeding risk in patients treated with anticoagulation using machine learning”. In the period of both research and clinical work she expanded her scientific net and realized that her passion to reach the optimal patient's treatment is much easier and more productive with international cooperation.`,
        links: [
            {
                url: "https://app.cristin.no/persons/show.jsf?id=1491203",
                display: "Cristin",
            },
            {
                url: "https://www.researchgate.net/profile/Aleksandra-Grdinic",
                display: "Research Gate",
            },
            //{
            //    url: "https://kobson.nb.rs/nauka_u_srbiji.132.html?autor=Grdinic%20Aleksandra%20G",
            //    display: "Kobson",
            //},
        ],
        isResearcher: false,
    },
    {
        id: 7,
        name: "Mashal Stanekzay",
        image: "/images/doctor_orange_mashal_stanekzay.jpg",
        shortBio: "Specialist in urology and general surgery",
        fullBio:
            "Mashal Stanekzay is a doctor from the Faculty of Medicine at the University of Bergen. He has several years of experience as a general practitioner and surgeon. He has worked at various surgical and medical departments at Haukeland University Hospital, Narvik Hospital, Kirkenes Hospital, Ålesund Hospital, St. Hospital and OUS. He is the author of the medical handbook. In addition, he has been a university lecturer at the University of Tromsø. Mashal is a specialist in both general surgery and urology. He now works at the urology department at Aker University Hospital.",
        links: null,
        isResearcher: false,
    },
    {
        id: 6,
        name: "Danilo Vojvodić",
        image: "/images/doctor_orange_danilo_vojvodic.jpg",
        shortBio: "Prof.dr Danilo Vojvodic-specialist in immunology",
        fullBio:
            "Danilo Vojvodić was born 1963 in Belgrade, Serbia, former Yugoslavia. He finished Medical School at 1988 at Belgrade University. 1993, after completing specialization in immunology at Military Medical Academy (MMA), Belgrade, Serbia, he was posted in Department for Clinical and Experimental Immunology, Institute for Medical Research. From 2002 he is a head of the Department for Clinical and Experimental Immunology, IMR MMA. From 2013 he is full time Professor of immunology at Medical School, University of Defense. From 2019 - 2020 he was a Director of the Institute for Medical Research Military Medical Academy (MMA).",
        links: [
            {
                url: "https://scholar.google.com/citations?hl=en&user=8zas_i4AAAAJ&view_op=list_works&sortby=pubdate",
                display: "Google Scholar",
            },
        ],
        isResearcher: true,
    },
    {
        id: 8,
        name: "José Fábio Santos Duarte Lana",
        image: "/images/doctor_orange_jose_fabio.jpg",
        shortBio: "Orthopedist and pain interventionist ",
        fullBio: `Dr. José Fábio Santos Duarte Lana is a physician and researcher specializing in Orthopedics and Traumatology, graduated from the Federal University of Minas Gerais (UFMG). With extensive experience in the field, he works as an Orthopedist and Pain Interventionist and is also a renowned researcher in Regenerative Medicine. Currently, Dr. José Fábio serves as the Director and Founder of the Lato Sensu Postgraduate Program in Regenerative Medicine – Orthoregen, as well as the Director and Founder of BIRM – Brazilian Institute of Regenerative Medicine. His professional activities include leading educational programs and initiatives focused on innovation in regenerative therapies.`,
        links: [
            {
                url: "https://www.scilit.com/publications?qa=[{%22id%22:%22author_unique_id%22,%22value%22:%2218535672%22}]&sort=newest&facet={%22publication_type%22:[%22JOURNAL-ARTICLE%22]}",
                display: "Scilit",
            },
        ],
        isResearcher: true,
    },
    {
        id: 5,
        name: "Aleksandar Grdinić",
        image: "/images/doctor_orange_aleksandar_grdinic.jpg",
        shortBio:
            "Specialist in gynecology/obstetrics and Fetal Maternal medicine",
        fullBio:
            "Aleksandar Grdinic, 51 years, Montenegro. He graduated the Faculty of Medicine in Belgrade (9.57 out of 10). Extensive experience as specialist 19 years and subspecialist 9 years. Specialization in Gynecology and Obstetrics, GAK Narodni Front, Belgrade (7 000 births per year) and subspecialization in Fetal Maternal medicine. After completed specialization he was posted in Clinical Center of Montenegro (3500 birth per year) Department for high risk pregnancies. In 2018 he was a Head of the Department. Also, he was a Head of council for prenatal diagnosis-president and Head of council for prenatal ultrasound detection of anomalies at University clinic in Podgorica, Lecturer of Ian Donald school of ultrasound. Mentor for fellowship in gynecology Medical faculty in Belgrade and a member of Commission of the Ministry of Health for national exam in gynecology in Montenegro. Aleksandar is dedicated to research and has international cooperation with colleagues in clinical work and in publications.",
        links: [
            {
                url: "https://www.researchgate.net/profile/Aleksandar-Grdinic",
                display: "Research Gate",
            },
        ],
        isResearcher: true,
    },
    {
        id: 20,
        name: "Junmyung Kwon",
        image: "/images/doctor_orange_junmyung_kwon.png",
        shortBio:
            "Emergency medicine specialist (MD.) and Computer scientist (MS.)",
        fullBio: `Is a critical care and emergency medicine physician and data scientist researching the application of
                artificial intelligence (AI) in medical domains. CEO of Medical AI Co., Ltd. and the Chief
                Director of the AI and Big Data Center at Sejong Medical Research Institute. Focused on the
                following research fields: raw signal electrocardiography (single-lead or 12-lead) processing,
                development of AI algorithm-based electrocardiography, endpoint prediction in critical care,
                emergency medicine, resuscitation, cardiology using AI, and AI-based clinical decision support
                systems.`,
        links: [
            {
                url: "https://scholar.google.co.kr/citations?user=DMd-2NEAAAAJ&hl=ko",
                display: "Google Scholar",
            },
        ],
        isResearcher: true,
    },
    {
        id: 9,
        name: "Aleksandra Šljivić",
        image: "/images/doctor_orange_aleksandra_sljivic.jpg",
        shortBio: "PhD, specialist in internal medicine and cardiology",
        fullBio: `Dr Aleksandra Sljivic is a cardiologist at the University Hospital Dr Dragisa Misovic- Dedinje, Belgrade, Serbia. At this esteemed institution, Dr Sljivic combines her clinical expertise with a strong focus on patient care, playing a crucial role in diagnosing and treating various cardiovascular conditions.
        With a background in both academic research and clinical practice, Dr Sljivic provides exceptional care for patients dealing with complex heart conditions. She is involved in the diagnosis and management of a wide range of cardiovascular diseases, including coronary artery disease, heart failure, arrhythmias, and valvular disorders.
        In addition to her clinical responsibilities, Dr Sljivic is engaged in cardiovascular research, contributing to advancements in both diagnostics and therapeutic methods in the field of cardiology. She is committed to staying up-to-date with the latest scientific developments and actively integrates new techniques and technologies into her clinical practice, ensuring her patients benefit from the most effective treatments available.
        Dr Sljivic has significant experience in echocardiography, a critical diagnostic tool in cardiology that allows for detailed imaging of the heart’s structure and function.
        Furthermore, her ability to teach young cardiologists includes guidance in the proper use of echocardiography, helping the next generation of healthcare professionals develop strong diagnostic skills in this essential area of cardiology.
        She obtained professional training in the field of stress echocardiography at the National Research Council in Pisa, Italy, also in the field of 3D echocardiography at Departmant of Cardiac, Thoracic and Vascular Sciences “, Padua, Italy.`,
        links: [
            {
                url: "https://www.researchgate.net/scientific-contributions/Aleksandra-Sljivic-2030282719",
                display: "Research Gate",
            },
        ],
        isResearcher: true,
    },
    {
        id: 4,
        name: "Shaikha Almazrouei Ph.D.",
        image: "/images/doctor_orange_shaikha_almazrouei.png",
        shortBio:
            "Dr. Shaikha Almazrouei, is a researcher in the field of Genomics...",
        fullBio:
            "Head of UAE Stem Cell Group. Dr. Shaikha Almazrouei, is a researcher in the field of Genomics and regenerative medicine. Dr. Almazrouei received her PhD from King's College London, where she studied liver transplant using hepatocyte microbes. She also holds an MSC from UAEU, where she researched the genetic basis of familial hypercholesterolemia, and a Bachelor's degree in cellular and molecular biology from the same institution. She is a member of the International Stem Cells Research Association (ISCCR) and the International Society for Cell & Gene Therapy. Dr.Almazrouei is committed to advancing research in the field of biotechnology. She also serves as the Head of the UAE Stem Cell Group and as a Lead Researcher with the Biotechnology Research Centre at the Abu Dhabi Technology Innovation Institute. Dr. Almazrouei has contributed to several high-profile projects, including the establishment of the Emirate Genome with M42, the establishment of DNA fingerprinting for the UAE Armed Forces and AD police fingerprint unit, and as a research assistant at Khalifa University. Dr. Almazrouei is dedicated to empowering biotechnology researchers and fostering innovation in the UAE. Her research interests include genomics and regenerative medicine.",
        links: [
            {
                url: "https://www.linkedin.com/in/dr-shaikha-almazrouei-14156116b/",
                display: "Linkedin",
            },
        ],
        isResearcher: true,
    },
    {
        id: 10,
        name: "Nemanja Lunić",
        image: "/images/doctor_orange_nemanja_lunic.jpg",
        shortBio:
            "Nemanja Lunić is a specialists in sports Medicine and Physiotherapy",
        fullBio: `Nemanja Lunić is a strength and rehab coach with over ten years of experience in strength training and rehabilitation for both athletes and the general population. With extensive international experience, he has spent the past five years working in Dubai, where he founded Revive Strength and Health, a company dedicated to fitness, rehabilitation, and performance optimization. He holds a degree in Medical Rehabilitation and a master’s degree in Sports Medicine and Physiotherapy.

      His expertise includes working with individuals dealing with musculoskeletal injuries, chronic pain, metabolic disorders, and cardiovascular conditions, as well as those looking to improve body composition. He believes that strength training and overall conditioning are essential for preventing and treating various injuries and health issues, while also enhancing mental well-being and boosting confidence.`,
        isResearcher: true,
    },
    {
        id: 2,
        name: "Fatima Jamali Ph.D.",
        image: "/images/doctor_orange_fatima_jamali.png",
        shortBio:
            "Fatima Abbas al Jamali is the head of Neuroscience Research department...",
        fullBio:
            "Founder & CEO CELL LAB 7. Stem Cell Bioprocess Advisor. Former Head of Neuroscience Research at Cell Therapy Center, Jordan University. Fatima Abbas al Jamali is the head of Neuroscience Research department at the the Cell Therapy Centre, University of Jordan. She is member of the Jordanian Stem Cells National Committee. Fatima has been working in the centre since November 2012. She holds a PhD in stem cell biology with a background in molecular biology and genetic engineering. She worked as a researcher in the Brain Research Institute in the ETH Zurich for more than 2 years. She coordinated during the last 3 years, the first clinical trial conducted in the CTC (ClinicalTrials.gov Identifier: NCT01895439). This trial aimed at treating multiple sclerosis patients who had failed all available treatments with stem cells. This work has been published last October in the CNS neuroscience and therapeutics Journal. On the basic research side, she has been active in identifying the neuronal differentiation potential of Mesenchymal stem cells and their immune-modulation. She is a co-author of a book chapter entitled: Stem Cell Therapies in Neurological Disorders published by Springer in 2017.",
        links: [
            {
                url: "https://www.linkedin.com/in/fatima-jamali-phd-a445ab44/",
                display: "Linkedin",
            },
        ],
        isResearcher: true,
    },
    {
        id: 21,
        name: "Jovana Manistasevic",
        image: "/images/doctor_orange_jovana.png",
        shortBio: "Specialist in psychiatry",
        fullBio:
            "Jovana is an experienced doctor in general practice and emergency medicine. She is currently a physician at the University Hospital Ahus in Norway, specializing in psychiatry, Lis 3.",
        links: null,
        isResearcher: false,
    },
    //{
    //    id: 1,
    //    name: "Behnam Sadeghi",
    //    image: "/images/male_silhouette.jpg",
    //    shortBio:
    //        "Behnam Sadeghi graduated as medical doctor (MD) in 1995. Dr Sadeghi...",
    //    fullBio:
    //        "Behnam Sadeghi graduated as medical doctor (MD) in 1995. Dr Sadeghi did his PhD in transplantation Immunology in 2009 at Karolinska Institute, Stockholm, Sweden. Since then, Dr Sadeghi’s research concentrated in the field of cell therapy and regenerative medicine both in clinical practice and experimental models. Dr Sadeghi established and applied decidua stromal cells (DSC) for the treatment of steroid refractory GVHD, ARDS and other acute inflammatory disorders in man. Other cell therapy //modalities including bone marrow, adipose derived mesenchymal stromal cells (BM, Ad-MSC) are among his research interest. Currently, Dr Sadeghi is head of Translational Cell therapy Research group (TCR) at the department of CLINTEC, Karolinska Institute, Stockholm, Sweden.",
    //},
];
</script>

<style scoped></style>
